import { HAS_IN_APP_AUTH_PAGES, LOGIN_URL } from '@/constants/auth'

const TRUTHY_VALUES = ['true', '1', 'yes']
const POST_LOGIN_REDIRECT_STORAGE_KEY = 'sweetspot.post_login_redirect'
const LAST_LOGIN_METHOD_STORAGE_KEY = 'sweetspot.last_login_method'
const PENDING_LOGIN_METHOD_STORAGE_KEY = 'sweetspot.pending_login_method'
const LAST_LOGIN_METHOD_COOKIE_KEY = 'sweetspot_last_login_method'
const LOGIN_METHOD_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30

export type LoginMethod = 'password' | 'sso'

const serializeLoginMethodCookie = (method: LoginMethod | null) => {
  if (!method) {
    return `${LAST_LOGIN_METHOD_COOKIE_KEY}=; Path=/; Max-Age=0; SameSite=Lax`
  }

  const secure =
    typeof window !== 'undefined' && window.location.protocol === 'https:'
      ? '; Secure'
      : ''

  return `${LAST_LOGIN_METHOD_COOKIE_KEY}=${method}; Path=/; Max-Age=${LOGIN_METHOD_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`
}

const persistLastLoginMethodCookie = (method: LoginMethod | null) => {
  if (typeof document === 'undefined') {
    return
  }

  document.cookie = serializeLoginMethodCookie(method)
}

export const isTruthyParam = (value: string | string[] | undefined): boolean =>
  typeof value === 'string' && TRUTHY_VALUES.includes(value.toLowerCase())

export const REDIRECT_PARAM_NAMES = [
  'redirect',
  'redirectTo',
  'returnTo',
  'return_to',
] as const

/**
 * Gets the post-login redirect URL from the query parameters
 *
 * @param query - Router query object
 * @returns The post-login redirect URL, or null if not provided
 */
export const getPostLoginRedirectUrlFromQuery = (
  query: Record<string, string | string[] | undefined>,
): string | null => {
  for (const paramName of REDIRECT_PARAM_NAMES) {
    const redirect = query[paramName]
    if (!redirect) {
      continue
    }

    const redirectUrl = Array.isArray(redirect) ? redirect[0] : redirect
    if (redirectUrl) {
      return redirectUrl
    }
  }

  return null
}

export const getPostLoginRedirectUrlFromSearch = (
  search: string,
): string | null => {
  const params = new URLSearchParams(search)
  for (const paramName of REDIRECT_PARAM_NAMES) {
    const redirectUrl = params.get(paramName)
    if (redirectUrl) {
      return redirectUrl
    }
  }

  return null
}

export const resolvePostLoginRedirectUrl = (
  redirectUrl: string | null | undefined,
): string | null => {
  const currentRedirectUrl =
    redirectUrl ??
    (typeof window === 'undefined'
      ? null
      : getPostLoginRedirectUrlFromSearch(window.location.search))

  if (!currentRedirectUrl || !isValidRedirectUrl(currentRedirectUrl)) {
    return null
  }

  return currentRedirectUrl
}

/**
 * Determines if a redirect URL is valid and safe
 *
 * @param url - URL to validate
 * @returns Boolean indicating if the URL is valid and safe
 */
const normalizeOrigin = (value: string): string | null => {
  try {
    return new URL(value).origin
  } catch {
    return null
  }
}

const getAllowedPostLoginRedirectOrigins = (): Set<string> =>
  new Set(
    (process.env.NEXT_PUBLIC_ALLOWED_POST_LOGIN_REDIRECT_ORIGINS ?? '')
      .split(',')
      .map((origin) => normalizeOrigin(origin.trim()))
      .filter((origin): origin is string => Boolean(origin)),
  )

export const isValidRedirectUrl = (url: string): boolean => {
  // If URL is a relative path (starts with / but not //), it's valid.
  // Protocol-relative URLs (//evil.com) must be rejected.
  if (url.startsWith('/') && !url.startsWith('//')) {
    return true
  }

  try {
    // Check if URL is part of the same origin or an explicitly allowlisted
    // companion app origin (for example static DARIUS returning through the
    // static console login page). Keep this list explicit to avoid open redirects.
    const urlObj = new URL(url)
    const currentOrigin = window.location.origin

    return (
      urlObj.origin === currentOrigin ||
      getAllowedPostLoginRedirectOrigins().has(urlObj.origin)
    )
  } catch (error) {
    // If URL parsing fails, it's invalid
    return false
  }
}

/**
 * Converts a redirect URL to a safe navigation target.
 * Same-origin absolute URLs are converted to their path. Explicitly allowlisted
 * cross-origin URLs are returned as absolute URLs. Other external URLs are
 * rejected, returning '/'.
 */
export const getSafeRedirectPath = (url: string | null): string => {
  if (!url) return '/'
  if (url.startsWith('/') && !url.startsWith('//')) return url
  try {
    const urlObj = new URL(url)
    if (urlObj.origin === window.location.origin) {
      return urlObj.pathname + urlObj.search + urlObj.hash
    }
    if (getAllowedPostLoginRedirectOrigins().has(urlObj.origin)) {
      return urlObj.toString()
    }
  } catch {
    // Invalid URL
  }
  return '/'
}

const createUrlWithRedirect = (
  basePath: string,
  redirectUrl: string | null,
  extraParams?: Record<string, string>,
): string => {
  const params = new URLSearchParams()
  if (redirectUrl && isValidRedirectUrl(redirectUrl)) {
    params.set('redirect', redirectUrl)
  }
  if (extraParams) {
    for (const [key, value] of Object.entries(extraParams)) {
      params.set(key, value)
    }
  }
  const qs = params.toString()
  return qs ? `${basePath}?${qs}` : basePath
}

export const createLoginUrlWithRedirect = (
  redirectUrl?: string | null,
  extraParams?: Record<string, string>,
): string =>
  // Keycloak has no in-app login page — start the flow at the BFF (LOGIN_URL) so callers
  // (route guards, the session-expiry guard) never land on the PropelAuth /auth/login page.
  // Both branches thread ?redirect= through: the BFF /auth/login stashes it in the PKCE state
  // cookie and its callback lands the user back on that path after login.
  HAS_IN_APP_AUTH_PAGES
    ? createUrlWithRedirect('/auth/login', redirectUrl || null, extraParams)
    : createUrlWithRedirect(LOGIN_URL, redirectUrl || null, extraParams)

/** Sends the user to the login flow, returning them to the current path after sign-in. */
export const redirectToLogin = (): void => {
  if (typeof window === 'undefined') return

  const currentPath = window.location.pathname + window.location.search
  // Already on the login page (PropelAuth in-app): nothing to do — avoid a redirect loop.
  if (currentPath.includes('/login')) return

  window.location.href = new URL(
    createLoginUrlWithRedirect(currentPath),
    window.location.origin,
  ).toString()
}

export const createSignupUrlWithRedirect = (
  redirectUrl: string | null,
  extraParams?: Record<string, string>,
): string => createUrlWithRedirect('/auth/signup', redirectUrl, extraParams)

export const createConfirmEmailUrlWithRedirect = (
  redirectUrl: string | null,
  extraParams?: Record<string, string>,
): string =>
  createUrlWithRedirect('/auth/confirm-email', redirectUrl, extraParams)

export const createVerifyMfaUrlWithRedirect = (
  redirectUrl: string | null,
): string => createUrlWithRedirect('/auth/verify-mfa', redirectUrl)

export const createSetupMfaUrlWithRedirect = (
  redirectUrl: string | null,
): string => createUrlWithRedirect('/auth/setup-mfa', redirectUrl)

const hasSessionStorage = () =>
  typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined'

const hasLocalStorage = () =>
  typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'

const isLoginMethod = (value: string | null): value is LoginMethod =>
  value === 'password' || value === 'sso'

export const getLoginMethodFromCookieHeader = (
  cookieHeader: string | undefined,
): LoginMethod | null => {
  if (!cookieHeader) {
    return null
  }

  for (const part of cookieHeader.split(';')) {
    const [rawKey, ...rawValueParts] = part.trim().split('=')
    if (rawKey !== LAST_LOGIN_METHOD_COOKIE_KEY) {
      continue
    }

    const value = rawValueParts.join('=')
    return isLoginMethod(value) ? value : null
  }

  return null
}

export const persistPostLoginRedirectUrl = (redirectUrl: string | null) => {
  if (!hasLocalStorage() && !hasSessionStorage()) {
    return
  }

  if (!redirectUrl || !isValidRedirectUrl(redirectUrl)) {
    if (hasLocalStorage()) {
      window.localStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY)
    }
    if (hasSessionStorage()) {
      window.sessionStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY)
    }
    return
  }

  if (hasLocalStorage()) {
    window.localStorage.setItem(POST_LOGIN_REDIRECT_STORAGE_KEY, redirectUrl)
  }
  if (hasSessionStorage()) {
    window.sessionStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY)
  }
}

export const getPersistedPostLoginRedirectUrl = (): string | null => {
  if (!hasLocalStorage() && !hasSessionStorage()) {
    return null
  }

  const redirectUrl =
    (hasLocalStorage()
      ? window.localStorage.getItem(POST_LOGIN_REDIRECT_STORAGE_KEY)
      : null) ??
    (hasSessionStorage()
      ? window.sessionStorage.getItem(POST_LOGIN_REDIRECT_STORAGE_KEY)
      : null)

  if (!redirectUrl || !isValidRedirectUrl(redirectUrl)) {
    if (hasLocalStorage()) {
      window.localStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY)
    }
    if (hasSessionStorage()) {
      window.sessionStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY)
    }
    return null
  }

  if (hasLocalStorage()) {
    window.localStorage.setItem(POST_LOGIN_REDIRECT_STORAGE_KEY, redirectUrl)
  }
  if (hasSessionStorage()) {
    window.sessionStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY)
  }

  return redirectUrl
}

export const resolveContinuationPostLoginRedirectUrl = (
  redirectUrl: string | null | undefined,
  allowPersistedPostLoginRedirect: boolean,
): string | null => {
  const currentRedirectUrl = resolvePostLoginRedirectUrl(redirectUrl)
  if (currentRedirectUrl) {
    return currentRedirectUrl
  }

  if (!allowPersistedPostLoginRedirect) {
    return null
  }

  return getPersistedPostLoginRedirectUrl()
}

export const clearPersistedPostLoginRedirectUrl = () => {
  if (!hasLocalStorage() && !hasSessionStorage()) {
    return
  }

  if (hasLocalStorage()) {
    window.localStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY)
  }
  if (hasSessionStorage()) {
    window.sessionStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY)
  }
}

export const consumePersistedPostLoginRedirectUrl = (): string | null => {
  const redirectUrl = getPersistedPostLoginRedirectUrl()
  if (!redirectUrl) {
    return null
  }

  clearPersistedPostLoginRedirectUrl()
  return redirectUrl
}

export const persistLastUsedLoginMethod = (method: LoginMethod | null) => {
  if (!hasLocalStorage()) {
    persistLastLoginMethodCookie(method)
    return
  }

  if (!method) {
    window.localStorage.removeItem(LAST_LOGIN_METHOD_STORAGE_KEY)
    persistLastLoginMethodCookie(null)
    return
  }

  window.localStorage.setItem(LAST_LOGIN_METHOD_STORAGE_KEY, method)
  persistLastLoginMethodCookie(method)
}

export const promotePendingLoginMethod = (): LoginMethod | null => {
  if (!hasLocalStorage()) {
    return null
  }

  const method = window.localStorage.getItem(PENDING_LOGIN_METHOD_STORAGE_KEY)
  window.localStorage.removeItem(PENDING_LOGIN_METHOD_STORAGE_KEY)

  if (!isLoginMethod(method)) {
    return null
  }

  window.localStorage.setItem(LAST_LOGIN_METHOD_STORAGE_KEY, method)
  persistLastLoginMethodCookie(method)
  return method
}

export const getLastUsedLoginMethod = (): LoginMethod | null => {
  if (!hasLocalStorage()) {
    return typeof document === 'undefined'
      ? null
      : getLoginMethodFromCookieHeader(document.cookie)
  }

  const method = window.localStorage.getItem(LAST_LOGIN_METHOD_STORAGE_KEY)
  if (!isLoginMethod(method)) {
    window.localStorage.removeItem(LAST_LOGIN_METHOD_STORAGE_KEY)
    return promotePendingLoginMethod()
  }

  return method
}

export const persistPendingLoginMethod = (method: LoginMethod | null) => {
  if (!hasLocalStorage()) {
    return
  }

  if (!method) {
    window.localStorage.removeItem(PENDING_LOGIN_METHOD_STORAGE_KEY)
    return
  }

  window.localStorage.setItem(PENDING_LOGIN_METHOD_STORAGE_KEY, method)
  persistLastLoginMethodCookie(method)
}

export const clearPendingLoginMethod = () => {
  if (!hasLocalStorage()) {
    return
  }

  window.localStorage.removeItem(PENDING_LOGIN_METHOD_STORAGE_KEY)
}

export const consumePendingLoginMethod = (): LoginMethod | null => {
  if (!hasLocalStorage()) {
    return null
  }

  const method = window.localStorage.getItem(PENDING_LOGIN_METHOD_STORAGE_KEY)
  window.localStorage.removeItem(PENDING_LOGIN_METHOD_STORAGE_KEY)

  return isLoginMethod(method) ? method : null
}
